import { FC } from 'react';
import { Playlist } from '@raydiant/api-client-js';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import PlaylistItemV2 from '../PlaylistItemV2';
import PlusIcon from '../PlaylistItemV2/PlusIcon';
import { useScheduleVisibility } from '../PlaylistItemV2/ScheduleVisibilityContext';
import emptyPlaylistIcon from '../../assets/empty-playlist-icon.svg';
import config from '../../../../../config';

const DASHBOARD_BASICS_ARTICLE_PATH =
  '/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC';
const DASHBOARD_BASICS_ARTICLE_URL = `https://help.displai.ai${DASHBOARD_BASICS_ARTICLE_PATH}`;

interface PlaylistItemListV2Props {
  playlist?: Playlist;
  onAddContent?: () => void;
}

const PlaylistItemListV2: FC<PlaylistItemListV2Props> = ({
  playlist,
  onAddContent,
}) => {
  const shouldHidePlaylist = useScheduleVisibility();
  const visibleItems =
    playlist?.items.filter((item) => !shouldHidePlaylist(item.playlist)) || [];
  const isEmpty = !playlist || visibleItems.length === 0;

  if (isEmpty) {
    return (
      <div className="flex flex-1 bg-white rounded-2xl">
        <div className="flex flex-1 justify-center items-center rounded-2xl p-4">
          <div className="max-w-[506px] flex flex-1 flex-col justify-center items-center rounded-2xl text-black">
            <div className="flex flex-col items-center gap-4">
              <img
                src={emptyPlaylistIcon}
                alt="Empty playlist"
                className="w-20 h-auto"
              />
              <Text variant="subheader" as="h2">
                Your playlist is a little empty
              </Text>
              <Text variant="body" as="p">
                Add some content and schedule it to fit your needs
              </Text>
            </div>

            <Button
              variant="primary"
              className="my-6 font-normal"
              onPress={onAddContent}
            >
              <div className="flex items-center gap-1">
                <PlusIcon size={14} />
                <span>Add content</span>
              </div>
            </Button>

            <Text variant="caption" as="p">
              Need help? Check out our{' '}
              <a
                href={
                  config.supportAppUrl
                    ? `${config.supportAppUrl}${DASHBOARD_BASICS_ARTICLE_PATH}`
                    : DASHBOARD_BASICS_ARTICLE_URL
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                help articles
              </a>
            </Text>
          </div>
        </div>
      </div>
    );
  }

  // Render playlist items using the same structure as the original PlaylistItemList
  const playlistItems = visibleItems.map((item) => (
    <PlaylistItemV2
      key={item.id}
      item={item}
      itemIdPath={[item.id]}
      playlistIdPath={[playlist.id]}
      parentPlaylist={playlist}
    />
  ));

  return (
    <div className="flex flex-col flex-1 bg-white rounded-2xl overflow-auto p-4">
      {/* Root-level Add content button */}
      <Button variant="primary" className="w-full mb-6" onPress={onAddContent}>
        <div className="flex items-center gap-1">
          <PlusIcon size={16} />
          <Text className="text-lg font-semibold">Add content</Text>
        </div>
      </Button>

      {/* Playlist items */}
      <div className="flex-1 overflow-auto p-1 -m-1">{playlistItems}</div>
    </div>
  );
};

export default PlaylistItemListV2;
